import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiClock, FiMusic, FiFileText, FiCalendar, FiPlay } from 'react-icons/fi';
import { AudioContentApi } from '../../services/api/AudioContentApi';
import AudioUrlService from '../../services/AudioUrlService';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { useAudio } from '../../contexts/AudioContext';
import './AudioDetailView.css';

/**
 * AudioDetailView - Detailed info view for a selected podcast or audiobook
 * 
 * Shown in the workspace when an audio item is selected from the sidebar list.
 * Features: editable title, delete, source documents, embedded audio player, metadata.
 */
const AudioDetailView = ({ audioItem, format, onBack, hideActions = false }) => {
  const { getDocumentById } = useData();
  const { addToast } = useToast();
  const { playAudio: globalPlayAudio, currentAudio, isPlaying } = useAudio();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(true);

  // Inline rename state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(audioItem.title || '');
  const [title, setTitle] = useState(audioItem.title || `Untitled ${format === 'podcast' ? 'Podcast' : 'Audiobook'}`);

  // Embedded audio player state
  const [streamUrl, setStreamUrl] = useState(null);
  const [urlLoading, setUrlLoading] = useState(false);

  const formatLabel = format === 'podcast' ? 'Podcast' : 'Audiobook';
  const formatIcon = format === 'podcast' ? '🎙️' : '📖';

  // Load stream URL when audioItem changes
  useEffect(() => {
    const docId = audioItem.documentId || audioItem.document_id;
    if (!docId) return;

    let cancelled = false;
    setStreamUrl(null);
    setUrlLoading(true);

    AudioUrlService.getOrRefreshUrl(docId)
      .then(url => {
        if (!cancelled) {
          setStreamUrl(url);
          setUrlLoading(false);
        }
      })
      .catch(err => {
        console.error('Failed to load stream URL:', err);
        if (!cancelled) setUrlLoading(false);
      });

    return () => { cancelled = true; };
  }, [audioItem.id, audioItem.documentId, audioItem.document_id]);

  // Load source documents (text documents used for generation, not the audio file)
  useEffect(() => {
    const sourceIds = audioItem.sourceDocumentIds || audioItem.source_document_ids || [];
    if (sourceIds.length === 0) {
      setDocLoading(false);
      return;
    }
    let cancelled = false;

    const loadDocs = async () => {
      setDocLoading(true);
      try {
        const docPromises = sourceIds.map(docId => getDocumentById(docId));
        const docs = await Promise.all(docPromises);
        if (!cancelled) setDocuments(docs.filter(Boolean));
      } catch (err) {
        console.warn('Failed to load source documents:', err);
        if (!cancelled) setDocuments([]);
      } finally {
        if (!cancelled) setDocLoading(false);
      }
    };

    loadDocs();
    return () => { cancelled = true; };
  }, [audioItem.sourceDocumentIds, audioItem.source_document_ids, getDocumentById]);

  // Sync title when audioItem prop changes
  useEffect(() => {
    setTitle(audioItem.title || `Untitled ${formatLabel}`);
    setEditTitle(audioItem.title || '');
  }, [audioItem.title, formatLabel]);

  const handleSaveTitle = useCallback(async () => {
    const trimmed = editTitle.trim();
    if (!trimmed || trimmed === title) {
      setIsEditing(false);
      setEditTitle(title);
      return;
    }

    try {
      await AudioContentApi.updateAudioContent(audioItem.id, { title: trimmed });
      setTitle(trimmed);
      addToast({ intent: 'success', message: `${formatLabel} renamed` });
    } catch (err) {
      console.error('Failed to rename audio:', err);
      addToast({ intent: 'danger', message: `Failed to rename ${formatLabel.toLowerCase()}` });
      setEditTitle(title);
    }
    setIsEditing(false);
  }, [editTitle, title, audioItem.id, formatLabel, addToast]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm(`Are you sure you want to delete this ${formatLabel.toLowerCase()}?`)) return;

    try {
      await AudioContentApi.deleteAudioContent(audioItem.id);
      addToast({ intent: 'success', message: `${formatLabel} deleted` });
      if (onBack) onBack();
    } catch (err) {
      console.error('Failed to delete audio:', err);
      addToast({ intent: 'danger', message: `Failed to delete ${formatLabel.toLowerCase()}` });
    }
  }, [audioItem.id, formatLabel, addToast, onBack]);

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hrs}h ${remainMins}m`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 0) return 'just now';
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const durationStr = formatDuration(audioItem.durationSeconds || audioItem.duration_seconds);
  const dateStr = formatDate(audioItem.created_at || audioItem.createdAt);

  return (
    <div className="audio-detail-view">
      {/* Header: Title + Actions */}
      <div className="adv-header">
        <div className="adv-title-row">
          {isEditing ? (
            <div className="adv-edit-title">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') { setIsEditing(false); setEditTitle(title); }
                }}
                onBlur={handleSaveTitle}
                className="adv-edit-input"
                autoFocus
              />
            </div>
          ) : (
            <h2 className="adv-title" onClick={() => { setIsEditing(true); setEditTitle(title); }}>
              <span className="adv-format-icon">{formatIcon}</span>
              {title}
              <FiEdit2 className="adv-title-edit-icon" size={16} />
            </h2>
          )}

          {!hideActions && (
          <div className="adv-actions">
            <button className="adv-delete-btn" onClick={handleDelete} title={`Delete ${formatLabel.toLowerCase()}`}>
              <FiTrash2 size={16} />
              <span>Delete</span>
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Info Pills */}
      <div className="adv-info-bar">
        <span className="adv-pill">
          <FiMusic size={13} />
          {formatLabel}
        </span>
        {durationStr && (
          <span className="adv-pill">
            <FiClock size={13} />
            {durationStr}
          </span>
        )}
        {dateStr && (
          <span className="adv-pill">
            <FiCalendar size={13} />
            {dateStr}
          </span>
        )}
      </div>

      {/* Play Button — delegates to global persistent AudioPlayer */}
      <div className="adv-player-section">
        {urlLoading ? (
          <div className="adv-player-loading">
            <div className="adv-loading-spinner"></div>
            <span>Loading audio...</span>
          </div>
        ) : streamUrl ? (
          <button
            className="adv-play-btn"
            onClick={() => {
              const docId = audioItem.documentId || audioItem.document_id;
              globalPlayAudio({
                id: audioItem.id,
                title: title,
                audioUrl: streamUrl,
                format,
                durationSeconds: audioItem.durationSeconds || audioItem.duration_seconds || 0,
                documentId: docId
              });
            }}
          >
            <FiPlay size={20} />
            <span>{currentAudio?.id === audioItem.id && isPlaying ? 'Now Playing' : 'Play in Global Player'}</span>
          </button>
        ) : (
          <div className="adv-player-unavailable">
            Audio not available
          </div>
        )}
      </div>

      {/* Source Documents */}
      <div className="adv-section">
        <h3 className="adv-section-title">Source Documents</h3>
        {docLoading ? (
          <div className="adv-loading-inline">Loading documents...</div>
        ) : documents.length === 0 ? (
          <div className="adv-empty-inline">No source documents linked</div>
        ) : (
          <div className="adv-doc-list">
            {documents.map(doc => (
              <div
                key={doc.id}
                className="adv-doc-card"
                onClick={() => navigate(`/document/${doc.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/document/${doc.id}`); } }}
              >
                <div className="adv-doc-card__icon">
                  <FiFileText size={18} />
                </div>
                <span className="adv-doc-card__name">{doc.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioDetailView;
